"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
exports.__esModule = true;
exports.default = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _objectWithoutPropertiesLoose2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutPropertiesLoose"));
var _react = _interopRequireDefault(require("react"));
var _classnames = _interopRequireDefault(require("classnames"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _styles = _interopRequireDefault(require("../styles"));
var _Chip = _interopRequireDefault(require("@mui/material/Chip"));
var _Close = _interopRequireDefault(require("@ellucian/ds-icons/lib/Close"));
var _tokens = require("../styles/tokens");
var _excluded = ["classes", "activated"];
var styles = function styles(theme) {
  return {
    root: {
      backgroundColor: theme.palette.grey[100],
      border: _tokens.borderWidthThin + " solid " + theme.palette.grey[400],
      color: theme.palette.grey[500],
      fontSize: _tokens.fontSizeDefault
    },
    activatedRoot: {
      backgroundColor: theme.palette.ctaColor.tint,
      border: _tokens.borderWidthThin + " solid " + theme.palette.ctaColor.base,
      color: theme.palette.ctaColor.base,
      '&:hover': {
        backgroundColor: theme.palette.ctaColor.tint
      },
      '&:active': {
        boxShadow: 'none',
        backgroundColor: theme.palette.ctaColor.tint
      }
    },
    label: {
      paddingLeft: _tokens.spacing40,
      paddingRight: _tokens.spacing40,
      paddingBottom: '0.125rem' // Push label up by 2px to perfectly center
    },

    labelWithIcon: {
      paddingRight: '0.75rem'
    },
    clickable: {
      WebkitTapHighlightColor: 'transparent',
      // Remove grey highlight
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: theme.palette.grey[100]
      },
      '&:focus': {
        backgroundColor: theme.palette.grey[100],
        boxShadow: "0 0 0 " + _tokens.borderWidthThick + " " + _tokens.colorBrandSecondary
      },
      '&:active': {
        backgroundColor: theme.palette.grey[300],
        boxShadow: 'none'
      },
      '&:active:focus': {
        boxShadow: 'none',
        backgroundColor: theme.palette.grey[300]
      },
      '&$activatedRoot': {
        backgroundColor: theme.palette.ctaColor.tint,
        border: _tokens.borderWidthThin + " solid " + theme.palette.ctaColor.base,
        color: theme.palette.ctaColor.base,
        '&:hover': {
          backgroundColor: theme.palette.ctaColor.tint
        },
        '&:focus': {
          backgroundColor: theme.palette.ctaColor.tint,
          boxShadow: "0 0 0 " + _tokens.borderWidthThick + " " + _tokens.colorBrandSecondary
        },
        '&:active': {
          boxShadow: 'none',
          backgroundColor: theme.palette.ctaColor.tint
        }
      }
    },
    deletable: {
      '&:focus': {
        backgroundColor: theme.palette.grey[100],
        boxShadow: "0 0 0 " + _tokens.borderWidthThick + " " + _tokens.colorBrandSecondary
      },
      '&$activatedRoot': {
        '&:focus': {
          backgroundColor: theme.palette.ctaColor.tint,
          boxShadow: "0 0 0 " + _tokens.borderWidthThick + " " + _tokens.colorBrandSecondary
        }
      }
    },
    deleteIcon: {
      boxSizing: 'initial',
      // required to fix stylistic differences between storybook and doc site
      margin: "0 0.3125rem 0 -" + _tokens.spacing30,
      height: _tokens.sizingSmall,
      width: _tokens.sizingSmall,
      color: theme.palette.grey[500],
      fill: theme.palette.grey[500],
      borderRadius: _tokens.borderRadiusCircle,
      padding: _tokens.spacing20,
      '&:hover': {
        backgroundColor: theme.palette.grey[300]
      },
      '&:active': {
        backgroundColor: theme.palette.grey[400]
      }
    }
  };
};

/**
 * Use `Chip` to represent a selected item or filter.
 * @done true
 * @updated v7.0.0
 * @versionAdded v0.0.8
 * @examples
 *  ChipExamples
 *  ChipActions
 *  ChipGroup
 *  ListActivatedChips
 *  SelectedContentWithChips
 */
var Chip = /*#__PURE__*/_react.default.forwardRef(function (props, ref) {
  var _cn, _cn2;
  var classes = props.classes,
    activated = props.activated,
    rest = (0, _objectWithoutPropertiesLoose2.default)(props, _excluded);
  return /*#__PURE__*/_react.default.createElement(_Chip.default, (0, _extends2.default)({
    classes: {
      root: (0, _classnames.default)(classes.root, (_cn = {}, _cn[classes.activatedRoot] = activated, _cn)),
      label: (0, _classnames.default)(classes.label, (_cn2 = {}, _cn2[classes.labelWithIcon] = props.onDelete, _cn2)),
      clickable: classes.clickable,
      deletable: classes.deletable,
      deleteIcon: classes.deleteIcon
    },
    deleteIcon: /*#__PURE__*/_react.default.createElement(_Close.default, null)
  }, rest, {
    ref: ref
  }));
});
Chip.muiName = 'Chip';
Chip.propTypes = {
  /**
   * @ignore
   * The `Avatar` element to display.
   */
  avatar: _propTypes.default.bool,
  /**
   * Set the chip to active, using the theme CTA color.
   */
  activated: _propTypes.default.bool,
  // Custom EDS prop
  /**
   * Override or extend the styles applied to the component.
   */
  classes: _propTypes.default.object,
  /**
   * @ignore
   */
  className: _propTypes.default.string,
  /**
   * If `true`, the chip will appear clickable, and will raise when pressed,
   * even if the onClick property is not defined. This can be used, for example,
   * along with the component property to indicate an anchor Chip is clickable.
   * If `false`, the chip will not appear clickable, even if onClick prop is defined.
   */
  clickable: _propTypes.default.bool,
  /**
   * @ignore
   * The color of the component.
   * It supports both default and custom theme colors, which can be added as shown in the
   * [palette customization guide](https://mui.com/material-ui/customization/palette/#adding-new-colors).
   */
  color: _propTypes.default.oneOfType([_propTypes.default.oneOf(['default', 'primary', 'secondary', 'error', 'info', 'success', 'warning']), _propTypes.default.string]),
  /**
   * The component used for the root node.
   * Either a string to use a DOM element or a component.
   */
  component: _propTypes.default.elementType,
  /**
   * @ignore
   * Override the default delete icon element. Shown only if `onDelete` is set.
   */
  deleteIcon: _propTypes.default.element,
  /**
   * @ignore
   * If `true`, the component is disabled.
   */
  disabled: _propTypes.default.bool,
  /**
   * @ignore
   * Icon element, shown in place of the `avatar`.
   *
   * The `avatar` prop has a higher precedence than this prop.
   */
  icon: _propTypes.default.element,
  /**
   * The content of the `Chip` label.
   */
  label: _propTypes.default.node,
  /**
   * Callback to fire when the `Chip` is clicked on or `space` or `enter` keys are pressed when the Chip has focus.
   */
  onClick: _propTypes.default.func,
  /**
   * Callback function fired when the delete icon is clicked or `backspace` key is pressed when the `Chip` has focus.
   * If set, the delete icon will be shown.
   */
  onDelete: _propTypes.default.func,
  /**
   * @ignore
   */
  onKeyDown: _propTypes.default.func,
  /**
   * @ignore
   */
  onKeyUp: _propTypes.default.func,
  /**
   * @ignore
   * The size of the component.
   */
  size: _propTypes.default.oneOfType([_propTypes.default.oneOf(['medium', 'small']), _propTypes.default.string]),
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx: _propTypes.default.oneOfType([_propTypes.default.arrayOf(_propTypes.default.oneOfType([_propTypes.default.func, _propTypes.default.object, _propTypes.default.bool])), _propTypes.default.func, _propTypes.default.object]),
  /**
   * @ignore
   */
  tabIndex: _propTypes.default.oneOfType([_propTypes.default.number, _propTypes.default.string]),
  /**
   * @ignore
   * The variant to use.
   */
  variant: _propTypes.default.oneOfType([_propTypes.default.oneOf(['filled', 'outlined']), _propTypes.default.string])
};
Chip.displayName = 'Chip';
var _default = (0, _styles.default)(styles)(Chip);
exports.default = _default;